// NPM Imports
import React, { Component } from "react";
import { connect } from "react-redux";
import ReactHtmlParser from "react-html-parser";

// Components Imports
import CustomRevvEditor from "../CustomRevvEditor";

// Action Imports
import { EditorActions, QuotationActions } from "../../actions";
import { updateDocumentClickedComponentID } from "../../actions/DocumentActions/index";
import { updateToolpaneView } from "../../actions/ToolbarActions/toolbarActions";

// Internal Imports
import { DOCUMENTS, TEMPLATE } from "../../constants";
import {
  getClauseOptionView,
  getComponentFromInfo,
} from "../../utils/documentUtils";

// Destructuring
const { nukeEditorState } = EditorActions;
const { CLAUSES_OPTIONS, FREE_TEXT_VIEW } = TEMPLATE;
const { DOCUMENT_MODE } = DOCUMENTS;
const {
  updateToolPaneData,
  setSectionDataForActiveSignatureInRightToolPane,
} = QuotationActions;

// Internal Methods
const handleTemplateClause = (clauseOptions, clauseOptionsDetails) => {
  let html = null;
  const { value } = clauseOptions;
  // check if it is a clause option, replace it with default option
  const { meta: { clauses: optionValue, subtype } = {} } =
    clauseOptionsDetails || {};
  if (subtype === CLAUSES_OPTIONS.FREE_TEXT) {
    html = ReactHtmlParser(
      value
        ? getClauseOptionView(value, DOCUMENT_MODE.TEMPLATE)
        : FREE_TEXT_VIEW,
    );
  } else if (!value || (Array.isArray(value) && value.length === 0)) {
    html = ReactHtmlParser(
      getClauseOptionView(optionValue[0].value, DOCUMENT_MODE.TEMPLATE),
    );
  } else if (typeof value === "string") {
    const valueOption = optionValue.find(option => option.id === value);
    html = valueOption
      ? ReactHtmlParser(
          getClauseOptionView(valueOption.value, DOCUMENT_MODE.TEMPLATE),
        )
      : null;
  } else if (Array.isArray(value)) {
    const valueOption = optionValue.filter(option => value.includes(option.id));
    html = valueOption.map(v =>
      ReactHtmlParser(getClauseOptionView(v.value, DOCUMENT_MODE.TEMPLATE)),
    );
  }
  return html;
};

const handleDocumentClause = (clauseOptions, clauseOptionsDetails) => {
  let html = null;

  // check if it is a clause option, replace it with default option
  const { value } = clauseOptionsDetails || {};
  if (!value || (Array.isArray(value) && value.length === 0)) {
    html = null;
  } else if (Array.isArray(value)) {
    html = value.map(v => (
      <div className="document-mode-clause-options">
        {ReactHtmlParser(getClauseOptionView(v.value))}
      </div>
    ));
  } else if (value && value.value) {
    html = ReactHtmlParser(getClauseOptionView(value.value));
  }
  return html;
};

class CustomText extends Component {
  state = {
    isHoveredComponentActive: false,
  };

  componentDidMount() {
    // Need to update "isHoveredComponentActive",
    // to handle the focus of newly added text component
    if (
      !this.props.isLocked &&
      !this.props.section.lock &&
      this.props.isComponentActive &&
      !this.state.isHoveredComponentActive
    ) {
      this.setState({ isHoveredComponentActive: true });
    }
  }

  componentDidUpdate() {
    // Need to update "isHoveredComponentActive",
    // to handle the focus of currently active text(adding DF case)
    if (
      !this.props.isLocked &&
      this.props.isComponentActive &&
      !this.state.isHoveredComponentActive
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ isHoveredComponentActive: true });
    }
  }

  _onMouseOver = event => {
    event.stopPropagation();
    if (
      !this.props.isLocked &&
      !this.props.section.lock &&
      !this.state.isHoveredComponentActive
    ) {
      this.setState({ isHoveredComponentActive: true });
    }
  };

  _handleMouseLeave = () => {
    if (
      !this.props.isLocked &&
      !this.props.section.lock &&
      this.state.isHoveredComponentActive
    ) {
      this.setState({ isHoveredComponentActive: false });
    }
  };

  _handleViewModeClick = () => {
    const {
      clauseOptionsDetails,
      isLocked,
      isComponentActive,
      section,
    } = this.props;
    if (
      !isLocked &&
      !isComponentActive &&
      (section && section.clauseOptions && clauseOptionsDetails)
    ) {
      this.props.updateDocumentClickedComponentID(
        section.id,
        true,
        section.type,
      );
      this.props.createDocSectionRef(React.createRef());
    }
  };

  _handleOnBlur = () => {
    if (!this.props.isLocked) {
      const { isComponentActive, preventClickedComponentIdUpdate } = this.props;
      if (isComponentActive && preventClickedComponentIdUpdate) {
        this.props.updateDocumentClickedComponentID(null, true, null);
      }
    }
  };

  _handleEditModeClick = event => {
    event.stopPropagation();
    const { section } = this.props;
    if (!this.props.isLocked && !this.props.isComponentActive) {
      this.props.updateDocumentClickedComponentID(
        section.id,
        true,
        section ? section.type : null,
      );
      this.props.createDocSectionRef(React.createRef());
      // Need to update the "isHoveredComponentActive" to handle the
      // focus of text editor after unlocking the section
      if (section.lock && !this.state.isHoveredComponentActive) {
        this.setState({ isHoveredComponentActive: true });
      }
    }
  };

  _getClassName = () => {
    const { section, clauses, mode, clauseOptionsDetails } = this.props;
    const { clauseID, clauseOptions } = section;
    let className = "component-view-mode";
    if (clauseID && clauses) {
      className = `${className} clause-component`;
    } else if (clauseOptions && clauseOptionsDetails) {
      if (mode === "TEMPLATE") {
        className = `${className} clause-component`;
      }
    }
    return className;
  };

  _getProductNameSection = () => {
    const { section, clauses, mode, clauseOptionsDetails } = this.props;
    const { clauseID, clauseOptions } = section;
    let productName;
    if (clauseID && clauses) {
      if (mode === "TEMPLATE") {
        productName = clauses.name;
      } else if (mode === "DOCUMENT") {
        productName = clauses.products.join();
      }
    } else if (clauseOptions && clauseOptionsDetails) {
      if (mode === "TEMPLATE") {
        const {
          label,
          meta: { clauses: optionValue, subtype },
        } = clauseOptionsDetails;
        productName =
          subtype === CLAUSES_OPTIONS.FREE_TEXT
            ? `${label} ${subtype}`
            : `${label} ${subtype} (${optionValue.length})`;
      }
    }
    return productName ? (
      <div
        className="clause-product-name"
        data-test="cistomtext-clause-option-name">
        {productName}
      </div>
    ) : null;
  };

  render() {
    const { section } = this.props;
    const { lock, clauseID, clauseOptions } = section;

    let isEditMode = false;
    if (
      !(this.props.mode === "DOCUMENT" && (clauseID || clauseOptions)) &&
      !(this.props.mode === "TEMPLATE" && clauseOptions)
    ) {
      isEditMode = true;
    }
    return (
      <div
        className={`customtext-component ${
          this.props.isComponentActive ? "active-customtext" : ""
        }`}
        onClick={isEditMode ? this._handleEditModeClick : () => {}}
        onBlur={this._handleOnBlur}
        onKeyPress={() => {}}
        role="button"
        tabIndex={0}>
        {isEditMode ? (
          // Editor View
          <div
            className="editor-component"
            data-test="customtext-editor-component"
            id={section.id}
            onMouseOver={this._onMouseOver}
            onMouseLeave={this._handleMouseLeave}
            onFocus={() => {}}
            onKeyPress={() => {}}
            role="button"
            tabIndex={0}>
            <CustomRevvEditor
              section={section}
              mode={this.props.mode}
              handleBlur={this.props.handleBlur}
              templateType={this.props.templateType}
              componentMeta={this.props.componentMeta}
              removeHighlightSelection={this.props.removeHighlightSelection}
              isComponentActive={this.props.isComponentActive}
              updateDocumentClickedComponentID={
                this.props.updateDocumentClickedComponentID
              }
              isSeparateToolbar
              isDocCustomTextEditor
              customToolbarRef={this.props.customToolbarRef}
              readOnly={lock || this.props.isLocked}
              isHoveredComponentActive={this.state.isHoveredComponentActive}
            />
          </div>
        ) : (
          // Non Editor View
          <div
            id={section.id}
            className={this._getClassName()}
            data-test="customtext-component-view-mode"
            onClick={this._handleViewModeClick}
            onKeyPress={() => {}}
            role="button"
            tabIndex={0}>
            {this._getProductNameSection()}
            {this.props.clauseOptionView &&
            this.props.section &&
            this.props.section.clauseOptions &&
            this.props.clauseOptionsDetails
              ? this.props.clauseOptionView
              : null}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { mode, clauseOptionsDetails, componentMeta } = props;
  // check if the section is in active state or hovered state
  const {
    components,
    clickedActiveComponentId,
    preventClickedComponentIdUpdate,
    clickedActiveComponentType,
  } = state.DocumentStore;
  const { addComponentType } = state.ToolPaneStore;
  let isComponentActive = false;
  // get section from redux
  const section =
    getComponentFromInfo(components, componentMeta) || props.component;
  if (clickedActiveComponentId === section.id) isComponentActive = true;
  const { clauseOptions } = section;
  let clauseOptionView;
  if (clauseOptions) {
    if (mode === DOCUMENT_MODE.TEMPLATE) {
      clauseOptionView = handleTemplateClause(
        clauseOptions,
        clauseOptionsDetails,
      );
    } else if (mode === DOCUMENT_MODE.DOCUMENT) {
      clauseOptionView = handleDocumentClause(
        clauseOptions,
        clauseOptionsDetails,
      );
    }
  }
  return {
    clauseOptionView,
    isComponentActive,
    clickedActiveComponentType,
    preventClickedComponentIdUpdate,
    addComponentType,
  };
};

const mapDispatchToProps = {
  updateDocumentClickedComponentID,
  updateToolpaneView,
  nukeEditorState,
  updateToolPaneData,
  setSectionDataForActiveSignatureInRightToolPane,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CustomText);



// WEBPACK FOOTER //
// ./src/components/DocumentItems/CustomText.js